/**
 * React Query hooks for Map Data API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mapDataService } from '../api';
import type { MapData } from '../api/types';

// Query Keys
export const mapDataKeys = {
  all: ['mapData'] as const,
  lists: () => [...mapDataKeys.all, 'list'] as const,
  list: (projectId: number) => [...mapDataKeys.lists(), projectId] as const,
};

/**
 * 맵 데이터 조회 (파싱된 객체)
 */
export function useMapData(projectId: number | null) {
  return useQuery({
    queryKey: mapDataKeys.list(projectId ?? -1),
    queryFn: async () => {
      if (!projectId) return null;

      console.log(`[useMapData] Fetching map data for projectId=${projectId}`);
      const response = await mapDataService.getMapDataParsed(projectId);
      if (!response.success) {
        console.error(`[useMapData] API failed: ${response.message}`);
        throw new Error(response.message);
      }
      const data = response.data || [];
      console.log(`[useMapData] Got ${data.length} records`, data.length > 0 ? {
        firstFloorCount: data[0]?.map_data?.floors?.length ?? 0,
        hasGraphJson: !!data[0]?.map_data?.floors?.[0]?.graphJson,
        graphJsonCells: data[0]?.map_data?.floors?.[0]?.graphJson?.cells?.length ?? 0,
      } : 'empty');
      return data;
    },
    enabled: !!projectId,
    staleTime: 0,
  });
}

/**
 * 맵 데이터 생성
 */
export function useCreateMapData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      mapData,
      templateId,
    }: {
      projectId: number;
      mapData: MapData;
      templateId?: number;
    }) => {
      const response = await mapDataService.createMapData(
        projectId,
        mapData,
        templateId
      );
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      // 해당 프로젝트의 맵 데이터 새로고침
      queryClient.invalidateQueries({
        queryKey: mapDataKeys.list(variables.projectId),
      });
    },
  });
}

/**
 * 맵 데이터 수정
 */
export function useUpdateMapData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      mapData,
    }: {
      projectId: number;
      mapData: MapData;
    }) => {
      const response = await mapDataService.updateMapData(projectId, mapData);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      // 해당 프로젝트의 맵 데이터 새로고침
      queryClient.invalidateQueries({
        queryKey: mapDataKeys.list(variables.projectId),
      });
    },
  });
}
